/**
 * Web Push no painel (#124 PR-3): estado do browser, subscrição do
 * dispositivo por canal (waiter/kitchen) e desativação.
 *
 * Regras da #124:
 *  - a permissão só é pedida DEPOIS de um clique (nunca ao abrir a página);
 *  - iOS só recebe push com a PWA instalada (decisão do PO) → estado
 *    `needs_install`, com a instrução de instalação no PushToggle;
 *  - um dispositivo não duplica subscrição (UNIQUE endpoint+canal na API —
 *    o POST faz upsert e devolve 201/200);
 *  - rotação das chaves VAPID invalida a subscrição antiga: com uma
 *    `applicationServerKey` diferente o painel desinscreve e subscreve de novo;
 *  - no logout a subscrição DESTE dispositivo é apagada ANTES de /auth/logout
 *    (`disablePush`, chamado por session.logoutSession — RGPD #148).
 *
 * Toda a comunicação passa pelo cliente HTTP (`lib/api.js`); erros NUNCA
 * derrubam o tempo real por WebSocket (fallback silencioso, critério 8).
 */
import { pushApi } from './api/push.js'
import type { PushChannel } from './api/push.js'

export type { PushChannel } from './api/push.js'

/** Estado de suporte/permissão do browser (o PushToggle traduz em texto). */
export type PushSupport = 'unsupported' | 'needs_install' | 'denied' | 'default' | 'granted'

const SW_PATH = '/sw.js'

/** iOS/iPadOS (Safari inclui Mac com tela tátil — iPadOS 13+). */
export function isIos(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent
  return /iP(hone|od|ad)/.test(ua) || (ua.includes('Macintosh') && nav.maxTouchPoints > 1)
}

/** A PWA está instalada (standalone)? iOS usa `navigator.standalone`. */
function isStandalone(nav: Navigator): boolean {
  const iosStandalone = (nav as Navigator & { standalone?: boolean }).standalone === true
  if (iosStandalone) return true
  try {
    return typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches
  } catch {
    return false
  }
}

/**
 * Suporte/permissão do browser atual (síncrono):
 *  - `needs_install`: iOS sem a PWA instalada — push do Safari só na app;
 *  - `unsupported`: sem Notification/serviceWorker/PushManager;
 *  - `denied`/`default`/`granted`: Notification.permission.
 */
export function pushSupport(nav: Navigator = navigator): PushSupport {
  // A limitação do iOS vem ANTES do check de PushManager: o Safari tem a
  // interface, mas só subscreve dentro da app instalada
  if (isIos(nav) && !isStandalone(nav)) return 'needs_install'
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in nav) ||
    typeof Notification === 'undefined' ||
    !('PushManager' in window)
  ) {
    return 'unsupported'
  }
  switch (Notification.permission) {
    case 'denied':
      return 'denied'
    case 'granted':
      return 'granted'
    default:
      return 'default'
  }
}

/** Registro do SW (o main.tsx registra no load; aqui cobre clique antecipado). */
async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  const existing = await navigator.serviceWorker.getRegistration()
  return existing ?? (await navigator.serviceWorker.register(SW_PATH))
}

/** Subscrição ativa deste dispositivo (null sem SW ou sem subscrição). */
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  const registration = await serviceWorkerRegistration()
  return (await registration?.pushManager.getSubscription()) ?? null
}

/** base64url (VAPID public key) → bytes para `applicationServerKey`. */
export function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replaceAll('-', '+').replaceAll('_', '/')
  const raw = atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

/** A subscrição existente usa ESTA chave VAPID? (rotação invalida a antiga) */
function sameServerKey(subscription: PushSubscription, publicKey: string): boolean {
  const key = urlBase64ToUint8Array(publicKey)
  const current = new Uint8Array(subscription.options.applicationServerKey as ArrayBuffer)
  if (current.length !== key.length) return false
  for (let i = 0; i < key.length; i++) if (current[i] !== key[i]) return false
  return true
}

/**
 * Ativa o push neste dispositivo para o canal (SÓ depois de um clique):
 * pede permissão → busca a chave VAPID → subscreve no SW → registra na API.
 * Lança `PushPermissionDeniedError` se a permissão não for concedida (o
 * PushToggle reavalia o estado e mostra «bloqueadas»); outros erros sobem
 * para o toast do chamador.
 */
export async function enablePush(channel: PushChannel): Promise<PushSubscription> {
  if (typeof Notification === 'undefined') throw new Error('Notifications não suportadas')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new PushPermissionDeniedError()

  const { public_key: publicKey } = await pushApi.vapidPublicKey()
  const registration = await serviceWorkerRegistration()
  if (!registration) throw new Error('Service worker não disponível')

  // rotação de chaves VAPID: subscrição de outra chave é inútil — troca
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameServerKey(subscription, publicKey)) {
    await subscription.unsubscribe()
    subscription = null
  }
  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  const json = subscription.toJSON()
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!p256dh || !auth) {
    await subscription.unsubscribe()
    throw new Error('Subscrição sem chaves p256dh/auth')
  }
  await pushApi.subscribe({
    endpoint: subscription.endpoint,
    keys: { p256dh, auth },
    channel,
    user_agent: navigator.userAgent,
  })
  return subscription
}

/** Permissão recusada ou dispensada — estado vira `denied`, não é erro de rede. */
export class PushPermissionDeniedError extends Error {
  constructor() {
    super('Permissão de notificações não concedida')
    this.name = 'PushPermissionDeniedError'
  }
}

/**
 * Desativa o push deste dispositivo: DELETE na API (idempotente, apaga a(s)
 * linha(s) do endpoint) e só depois `unsubscribe()` local. Best effort:
 * falha de rede não propaga — a linha órfã é apagada pelo 404/410 do envio
 * (critério 9) ou pelo próximo upsert.
 */
export async function disablePush(): Promise<void> {
  try {
    const subscription = await currentPushSubscription()
    if (!subscription) return
    try {
      await pushApi.removeSubscription({ endpoint: subscription.endpoint })
    } catch {
      // API fora/sessão expirada: mantém o unsubscribe local (RGPD do dispositivo)
    }
    await subscription.unsubscribe()
  } catch {
    // sem service worker (ex.: página de login, happy-dom): nada a fazer
  }
}
